import { Routes, Route, Navigate } from 'react-router'
import { lazy, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useConnectionStore } from '@/stores/connectionStore'
import { useLastConnection } from '@/hooks/useLastConnection'
import { useAppSettings } from '@/hooks/useAppSettings'
import { LoginPage } from '@/pages/LoginPage'
import { OAuthCallback } from '@/pages/OAuthCallback'
import { MainLayout } from '@/components/layout/MainLayout'
import { ConnectionsPage } from '@/pages/ConnectionsPage'
import { QueryProvider } from '@/components/common/QueryProvider'
import { SettingsProvider } from '@/contexts/SettingsContext'
import AppErrorBoundary from '@/components/common/AppErrorBoundary'
import { LazyRoute } from '@/components/common/LazyRoute'
import { preloadMonacoEditor } from '@/utils/monacoPreload'

// Lazy-loaded page components for code splitting
// Using async arrow functions to satisfy eslint promise-function-async rule
const DatabaseExplorer = lazy(async () => ({ default: (await import('@/pages/DatabaseExplorer')).DatabaseExplorer }))
const UserManagement = lazy(async () => ({ default: (await import('@/pages/UserManagement')).UserManagement }))
const DatabaseUserManagement = lazy(async () => ({ default: (await import('@/pages/DatabaseUserManagement')).DatabaseUserManagement }))
const RBACMatrix = lazy(async () => ({ default: (await import('@/pages/RBACMatrix')).RBACMatrix }))
const SessionsManager = lazy(async () => ({ default: (await import('@/pages/SessionsManager')).SessionsManager }))
const SystemInformation = lazy(async () => ({ default: (await import('@/pages/SystemInformation')).SystemInformation }))
const Settings = lazy(async () => ({ default: (await import('@/pages/Settings')).Settings }))
const AgentsPage = lazy(async () => ({ default: (await import('@/pages/AgentsPage')).AgentsPage }))
const MonitoringPage = lazy(async () => ({ default: (await import('@/pages/MonitoringPage')).MonitoringPage }))
const ScyllaDBCDCPage = lazy(async () => ({ default: (await import('@/pages/ScyllaDBCDCPage')).ScyllaDBCDCPage }))
const ScyllaDBServiceLevelsPage = lazy(async () => ({ default: (await import('@/pages/ScyllaDBServiceLevelsPage')).ScyllaDBServiceLevelsPage }))

function SmartRedirect() {
  const { activeConnection } = useConnectionStore()

  // If there's an active connection, redirect to explorer, otherwise to connections
  if (activeConnection) {
    return <Navigate to={`/explorer/${activeConnection.id}`} replace />
  }

  return <Navigate to="/connections" replace />
}

// Component references to avoid JSX as props
const LoginPageComponent = LoginPage
const OAuthCallbackComponent = OAuthCallback
const NavigateToLogin = () => <Navigate to="/login" replace />
const NavigateToHome = () => <Navigate to="/" replace />
const MainLayoutComponent = MainLayout
const SmartRedirectComponent = SmartRedirect
const ConnectionsPageComponent = ConnectionsPage

// QueryEditor route placeholder - actual QueryEditor is rendered by MainLayout with keep-alive
// to prevent Monaco editor reinitialization on page switches
const QueryEditorPlaceholder = () => null

// Lazy-loaded component wrappers with Suspense + chunk-reload guard
const DatabaseExplorerComponent = () => (
  <LazyRoute>
    <DatabaseExplorer />
  </LazyRoute>
)
const UserManagementComponent = () => (
  <LazyRoute>
    <UserManagement />
  </LazyRoute>
)
const DatabaseUserManagementComponent = () => (
  <LazyRoute>
    <DatabaseUserManagement />
  </LazyRoute>
)
const RBACMatrixComponent = () => (
  <LazyRoute>
    <RBACMatrix />
  </LazyRoute>
)
const SessionsManagerComponent = () => (
  <LazyRoute>
    <SessionsManager />
  </LazyRoute>
)
const SystemInformationComponent = () => (
  <LazyRoute>
    <SystemInformation />
  </LazyRoute>
)
const SettingsComponent = () => (
  <LazyRoute>
    <Settings />
  </LazyRoute>
)
const AgentsPageComponent = () => (
  <LazyRoute>
    <AgentsPage />
  </LazyRoute>
)
const MonitoringPageComponent = () => (
  <LazyRoute>
    <MonitoringPage />
  </LazyRoute>
)
const ScyllaDBCDCPageComponent = () => (
  <LazyRoute>
    <ScyllaDBCDCPage />
  </LazyRoute>
)
const ScyllaDBServiceLevelsPageComponent = () => (
  <LazyRoute>
    <ScyllaDBServiceLevelsPage />
  </LazyRoute>
)

function AuthenticatedApp() {
  // Initialize app settings for authenticated user
  useAppSettings()

  // Restore last connection after user is authenticated
  useLastConnection()

  return (
    <div className="h-full">
      <Routes>
        <Route path="/" Component={MainLayoutComponent}>
        <Route index Component={SmartRedirectComponent} />
        <Route path="connections" Component={ConnectionsPageComponent} />
        <Route path="explorer/:connectionId" Component={DatabaseExplorerComponent} />
        <Route path="query/:connectionId" Component={QueryEditorPlaceholder} />
        <Route path="users" Component={UserManagementComponent} />
        <Route path="db-users/:connectionId" Component={DatabaseUserManagementComponent} />
        <Route path="rbac-matrix/:connectionId" Component={RBACMatrixComponent} />
        <Route path="sessions/:connectionId" Component={SessionsManagerComponent} />
        <Route path="system/:connectionId" Component={SystemInformationComponent} />
        <Route path="settings" Component={SettingsComponent} />
        <Route path="agents" Component={AgentsPageComponent} />
        <Route path="monitoring/:connectionId" Component={MonitoringPageComponent} />
        <Route path="cdc/:connectionId" Component={ScyllaDBCDCPageComponent} />
        <Route path="service-levels/:connectionId" Component={ScyllaDBServiceLevelsPageComponent} />
      </Route>
      <Route path="/login" Component={NavigateToHome} />
      <Route path="*" Component={NavigateToHome} />
      </Routes>
    </div>
  )
}

function App() {
  const { user, loadUserFromStorage } = useAuthStore()

  // Load user from storage on app start and preload Monaco
  useEffect(() => {
    loadUserFromStorage()
    // Preload Monaco Editor after a short delay to not block initial render
    const timeoutId = setTimeout(() => {
      preloadMonacoEditor()
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [loadUserFromStorage])

  if (!user) {
    return (
      <AppErrorBoundary>
        <div className="h-full">
          <QueryProvider>
            <Routes>
              <Route path="/login" Component={LoginPageComponent} />
              <Route path="/auth/callback" Component={OAuthCallbackComponent} />
              <Route path="/dashboard" Component={OAuthCallbackComponent} />
              <Route path="*" Component={NavigateToLogin} />
            </Routes>
          </QueryProvider>
        </div>
      </AppErrorBoundary>
    )
  }

  return (
    <AppErrorBoundary>
      <div className="h-full">
        <QueryProvider>
          <SettingsProvider>
            <AuthenticatedApp />
          </SettingsProvider>
        </QueryProvider>
      </div>
    </AppErrorBoundary>
  )
}

export default App
